
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { UserProfile } from '../types';
import { supabase } from '../supabase';
import { googleAuth } from '../utils/googleAuth';
import { sheetsService } from '../utils/googleSheets';

interface AuthContextType {
  isAuthenticated: boolean;
  isBlocked: boolean;
  isExpired: boolean;
  user: UserProfile | null;
  userStatus: string;
  isLoading: boolean;
  loadingMessage: string;
  spreadsheetId: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUserStatus: () => Promise<void>;
  setLoading: (loading: boolean, message?: string) => void;
  initAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isBlocked, setIsBlocked] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [userStatus, setUserStatus] = useState<string>('trial');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadingMessage, setLoadingMessage] = useState<string>('Sincronizando...');
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);

  const setLoading = useCallback((loading: boolean, message: string = 'Sincronizando...') => {
    setIsLoading(loading);
    setLoadingMessage(message);
  }, []);

  const refreshUserStatus = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const statusData = await sheetsService.checkUserStatus();
      if (!statusData.authorized) {
        if (statusData.blocked) {
          setIsBlocked(true);
          setIsAuthenticated(false);
        } else {
          await googleAuth.logout();
        }
        return;
      }

      setIsBlocked(statusData.is_blocked || false);
      setIsExpired(statusData.subscription_status === 'expired');
      setUserStatus(statusData.subscription_status);
      const sid = await sheetsService.getOrCreateSpreadsheet();
      
      if (sid) {
        setSpreadsheetId(sid);
        const userProfile: UserProfile = { 
          id: session.user.id, 
          email: session.user.email!, 
          full_name: statusData.full_name || session.user.user_metadata.full_name || 'Usuário', 
          google_sheets_id: sid,
          salt: statusData.encryption_salt,
          encryption_verify: statusData.encryption_verify,
          wrapped_key: statusData.encryption_wrapped_key,
          subscription_status: statusData.subscription_status,
          subscription_expiry: statusData.subscription_expiry,
          renewal_count: statusData.renewal_count || 0,
          is_blocked: statusData.is_blocked || false
        };
        setUser(userProfile);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error refreshing user status:', error);
    }
  }, []);

  const initAuth = useCallback(async () => {
    const isReturningFromAuth = window.location.hash.includes('access_token');
    if (isReturningFromAuth) {
      setLoading(true, 'Processando acesso...');
      await new Promise(r => setTimeout(r, 1500));
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      await refreshUserStatus();
    } catch (err) {
      console.error(err);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [refreshUserStatus, setLoading]);

  useEffect(() => {
    initAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        initAuth();
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setUser(null);
        setSpreadsheetId(null);
        // Do not clear everything, just vault key is handled by VaultProvider
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [initAuth]);

  const login = useCallback(async () => {
    await googleAuth.login();
  }, []);

  const logout = useCallback(async () => {
    await googleAuth.logout();
  }, []);

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      isBlocked,
      isExpired,
      user, 
      userStatus, 
      isLoading, 
      loadingMessage, 
      spreadsheetId,
      login,
      logout,
      refreshUserStatus,
      setLoading,
      initAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
